import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'

const isReadySubscribe = () => () => undefined
const isReadyGetSnapshot = () => true
const isReadyGetServerSnapshot = () => false

export const createStorageHook = <
  Keys extends string,
  DefaultTypes extends { [key in Keys]: unknown } = { [key in Keys]: unknown },
>(
  /** undefined の場合、SSRとして常にdefaultValue/fallbackValueとなる */
  storage: Storage | undefined,
  defaultValues: DefaultTypes
) => {
  // 変更検知時のリスナー
  const listeners: Map<Keys, Set<() => void>> = new Map()
  // 特定のkeyが変化したことを通知する関数
  const notifyChange = (key: Keys) => {
    listeners.get(key)?.forEach((listener) => listener())
  }
  if (storage) {
    // storageの変更は全てグローバルの１箇所で管理する
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.storageArea !== storage) return
      if (e.key === null) {
        // storageがクリアされた場合、全てのリスナーを呼び出す
        listeners.forEach((keyListeners) =>
          keyListeners.forEach((listener) => listener())
        )
      } else {
        // storageのkeyが変更された場合
        notifyChange(e.key as Keys)
      }
    })
  }
  return <Key extends Keys>(
    key: Key,
    /** SSR時に利用される値、未指定の場合defaultValueが利用される */
    fallbackValue = defaultValues[key]
  ): Readonly<{
    value: DefaultTypes[Key]
    set: Dispatch<SetStateAction<DefaultTypes[Key]>>
    reset: () => void
    /** ルーターフィールドがクライアント側で更新され、使用できる状態になっているかどうか。 useEffectメソッド内でのみ使用する必要があり、サーバーでの条件付きレンダリングには使用しないでください。 */
    isReady: boolean
  }> => {
    const prevRef = useRef<{ raw: string | null; val: DefaultTypes[Key] }>({
      raw: null,
      val: fallbackValue,
    })
    const handlers = useMemo(() => {
      return {
        subscribe: (listener: () => void) => {
          const keyListeners = listeners.get(key) ?? new Set()
          if (!listeners.has(key)) listeners.set(key, keyListeners)
          keyListeners.add(listener)
          return () => {
            keyListeners.delete(listener)
          }
        },
        getSnapshot: (): DefaultTypes[Key] => {
          const raw = storage?.getItem(key) ?? null
          // storage自体がない、もしくは既存の値がない場合、fallbackValueを返す
          if (raw === null) return fallbackValue
          // JSON.stringifyを使うとオブジェクトは同じでも異なる参照になってしまうので無限ループになりうる
          // storageに保存された文字列が同じ場合はキャッシュを返す
          if (prevRef.current.raw === raw) return prevRef.current.val
          try {
            const val = JSON.parse(raw)
            prevRef.current.raw = raw
            prevRef.current.val = val
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return val
          } catch {
            // JSON.parseに失敗した場合 = storageの値が不正な場合はstorageをクリアしてfallbackValueを返す
            storage?.removeItem(key)
            return fallbackValue
          }
        },
        // SSR時はstorageの値を取得できないので、fallbackValueを返す
        getServerSnapshot: (): DefaultTypes[Key] => fallbackValue,
      }
    }, [fallbackValue, key])
    const value = useSyncExternalStore<DefaultTypes[Key]>(
      handlers.subscribe,
      handlers.getSnapshot,
      handlers.getServerSnapshot
    )

    const set = useCallback<Dispatch<SetStateAction<DefaultTypes[Key]>>>(
      (update) => {
        const val =
          update instanceof Function ? update(handlers.getSnapshot()) : update
        try {
          storage?.setItem(key, JSON.stringify(val))
          notifyChange(key)
        } catch (e) {
          console.warn(e)
        }
      },
      [handlers, key]
    )

    const reset = useCallback(() => {
      storage?.removeItem(key)
      notifyChange(key)
    }, [key])

    const isReady = useSyncExternalStore(
      isReadySubscribe,
      isReadyGetSnapshot,
      isReadyGetServerSnapshot
    )

    return { value, set, reset, isReady }
  }
}
